/**
 * DebugOverview - Компонент загальної статистики для вкладки "Загальне"
 * Збирає та відображає найважливішу інформацію від усіх профайлерів
 */

import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    DatabaseOutlined,
    ExclamationCircleOutlined,
    InfoCircleOutlined,
    SyncOutlined,
    ThunderboltOutlined,
    WarningOutlined,
} from '@ant-design/icons'
import { Alert, Card, Col, Progress, Row, Space, Spin, Statistic, Tag, Timeline, Typography } from 'antd'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import { loggerManager, profilerManager } from '../../Managers'
import { DebugProfilerData } from './types'

const { Title, Text } = Typography

interface OverviewStats {
    systemStatus: 'healthy' | 'warning' | 'error'
    totalIssues: number
    loadersTotal: number
    loadersSuccessful: number
    loadersFailed: number
    avgLoadTime: number
    profiledComponents: number
    slowComponents: number
    memoryUsage?: number
    timestamp: number
}

interface SystemEvent {
    type: 'error' | 'warning' | 'info'
    title: string
    description: string
    timestamp: Date
    source?: string
}

export const DebugOverview: React.FC = observer(() => {
    const [stats, setStats] = useState<OverviewStats | null>(null)
    const [events, setEvents] = useState<SystemEvent[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        loadOverviewData()

        // Обновляем данные каждые 10 секунд
        const interval = setInterval(loadOverviewData, 10000)
        return () => clearInterval(interval)
    }, [])

    const loadOverviewData = async () => {
        setLoading(true)

        try {
            const stats = profilerManager.getStats()
            const logStats = loggerManager.getStats()
            const systemEvents: SystemEvent[] = []

            // Формируем данные из статистики менеджеров
            const profilersData: Record<string, DebugProfilerData> = {
                components: {
                    summary: {
                        status: stats.components.active > 0 ? 'success' : ('warning' as const),
                        count: stats.components.total,
                        label: `${stats.components.active} активных компонентов`,
                    },
                    details: {
                        stats: [
                            {
                                title: 'Всего компонентов',
                                value: stats.components.total,
                                description: 'Зарегистрированных в системе',
                            },
                            {
                                title: 'Активные',
                                value: stats.components.active,
                                description: 'Работающие компоненты',
                            },
                        ],
                    },
                },
                logs: {
                    summary: {
                        status:
                            logStats.errors > 0 ? 'error' : logStats.warnings > 0 ? 'warning' : ('success' as const),
                        count: logStats.total,
                        label: `${logStats.errors} ошибок, ${logStats.warnings} предупреждений`,
                    },
                    details: {
                        stats: [
                            {
                                title: 'Всего логов',
                                value: logStats.total,
                                description: 'Записей в системе',
                            },
                            {
                                title: 'Ошибки',
                                value: logStats.errors,
                                description: 'Критических проблем',
                            },
                        ],
                    },
                },
            }

            // Добавляем события от логгера
            loggerManager.getErrorEntries(5).forEach((entry) => {
                systemEvents.push({
                    type: 'error',
                    title: `Ошибка: ${entry.message}`,
                    description: entry.error?.message || 'Ошибка в системе',
                    timestamp: new Date(entry.timestamp),
                    source: entry.category,
                })
            })

            // Агрегируем статистику
            const overviewStats = aggregateStats(profilersData, systemEvents)
            setStats(overviewStats)
            setEvents(systemEvents)
        } catch (error) {
            console.error('Ошибка загрузки данных обзора:', error)
            setEvents([
                {
                    type: 'error',
                    title: 'Ошибка загрузки данных',
                    description: error instanceof Error ? error.message : 'Неизвестная ошибка',
                    timestamp: new Date(),
                },
            ])
        } finally {
            setLoading(false)
        }
    }

    const aggregateStats = (
        profilersData: Record<string, DebugProfilerData>,
        systemEvents: SystemEvent[]
    ): OverviewStats => {
        let totalIssues = 0
        let loadersTotal = 0
        let loadersSuccessful = 0
        let loadersFailed = 0
        let avgLoadTime = 0
        let profiledComponents = 0
        let slowComponents = 0

        // Анализируем данные каждого профайлера
        Object.entries(profilersData).forEach(([key, data]) => {
            if (data.summary) {
                // Считаем проблемы
                if (data.summary.status === 'error') totalIssues++
                if (data.summary.status === 'warning') totalIssues++
            }

            // Специальная обработка для лоадеров
            if (key.includes('loader') || key.includes('Loader')) {
                data.details.stats?.forEach((stat) => {
                    if (stat.title.toLowerCase().includes('всего')) {
                        loadersTotal += Number(stat.value) || 0
                    }
                    if (stat.title.toLowerCase().includes('успешных')) {
                        loadersSuccessful += Number(stat.value) || 0
                    }
                    if (stat.title.toLowerCase().includes('ошибками')) {
                        loadersFailed += Number(stat.value) || 0
                    }
                    if (stat.title.toLowerCase().includes('время')) {
                        avgLoadTime = Math.max(avgLoadTime, Number(stat.value) || 0)
                    }
                })
            }

            // Специальная обработка для компонентов
            if (key.includes('component') || key.includes('Component')) {
                data.details.stats?.forEach((stat) => {
                    if (stat.title.toLowerCase().includes('компонент')) {
                        profiledComponents += Number(stat.value) || 0
                    }
                    if (stat.title.toLowerCase().includes('медленн')) {
                        slowComponents += Number(stat.value) || 0
                    }
                })
            }
        })

        // Добавляем проблемы из событий
        totalIssues += systemEvents.filter((e) => e.type === 'error' || e.type === 'warning').length

        // Определяем общий статус системы
        let systemStatus: 'healthy' | 'warning' | 'error' = 'healthy'
        if (loadersFailed > 0 || systemEvents.some((e) => e.type === 'error')) {
            systemStatus = 'error'
        } else if (totalIssues > 0 || slowComponents > 0) {
            systemStatus = 'warning'
        }

        return {
            systemStatus,
            totalIssues,
            loadersTotal,
            loadersSuccessful,
            loadersFailed,
            avgLoadTime,
            profiledComponents,
            slowComponents,
            timestamp: Date.now(),
        }
    }

    const getSystemStatusColor = (status: string) => {
        switch (status) {
            case 'healthy':
                return '#52c41a'
            case 'warning':
                return '#faad14'
            case 'error':
                return '#ff4d4f'
            default:
                return '#d9d9d9'
        }
    }

    const getSystemStatusText = (status: string) => {
        switch (status) {
            case 'healthy':
                return 'Система работает нормально'
            case 'warning':
                return 'Обнаружены предупреждения'
            case 'error':
                return 'Обнаружены ошибки'
            default:
                return 'Статус неизвестен'
        }
    }

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>
                    <Text type="secondary">Сбор данных системы...</Text>
                </div>
            </div>
        )
    }

    if (!stats) {
        return <Alert message="Не удалось получить данные системы" type="error" showIcon />
    }

    return (
        <div style={{ padding: '16px' }}>
            <Space orientation="vertical" style={{ width: '100%' }} size="small">
                {/* Статус системы */}
                <Card>
                    <Row gutter={16} align="middle">
                        <Col span={4}>
                            <div style={{ textAlign: 'center' }}>
                                {stats.systemStatus === 'healthy' && (
                                    <CheckCircleOutlined style={{ fontSize: '48px', color: '#52c41a' }} />
                                )}
                                {stats.systemStatus === 'warning' && (
                                    <WarningOutlined style={{ fontSize: '48px', color: '#faad14' }} />
                                )}
                                {stats.systemStatus === 'error' && (
                                    <ExclamationCircleOutlined style={{ fontSize: '48px', color: '#ff4d4f' }} />
                                )}
                            </div>
                        </Col>
                        <Col span={20}>
                            <Title
                                level={3}
                                style={{
                                    margin: 0,
                                    color: getSystemStatusColor(stats.systemStatus),
                                }}
                            >
                                {getSystemStatusText(stats.systemStatus)}
                            </Title>
                            <Text type="secondary">
                                Последнее обновление: {new Date(stats.timestamp).toLocaleTimeString()}
                            </Text>
                            {stats.totalIssues > 0 && (
                                <div style={{ marginTop: 8 }}>
                                    <Tag color="orange">
                                        {stats.totalIssues} проблем
                                        {stats.totalIssues > 1 ? '' : 'а'} обнаружено
                                    </Tag>
                                </div>
                            )}
                        </Col>
                    </Row>
                </Card>

                {/* Основная статистика */}
                <Row gutter={16}>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Лоадеры"
                                value={stats.loadersSuccessful}
                                suffix={`/ ${stats.loadersTotal}`}
                                prefix={<DatabaseOutlined />}
                                styles={{
                                    content: { color: stats.loadersFailed > 0 ? '#ff4d4f' : '#3f8600' },
                                }}
                            />
                            {stats.loadersTotal > 0 && (
                                <Progress
                                    percent={Math.round((stats.loadersSuccessful / stats.loadersTotal) * 100)}
                                    size="small"
                                    status={stats.loadersFailed > 0 ? 'exception' : 'success'}
                                    style={{ marginTop: 8 }}
                                />
                            )}
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Время загрузки"
                                value={stats.avgLoadTime}
                                suffix="мс"
                                prefix={<ClockCircleOutlined />}
                                styles={{
                                    content: { color: stats.avgLoadTime > 1000 ? '#ff4d4f' : '#3f8600' },
                                }}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Компоненты"
                                value={stats.profiledComponents - stats.slowComponents}
                                suffix={`/ ${stats.profiledComponents}`}
                                prefix={<ThunderboltOutlined />}
                                styles={{
                                    content: { color: stats.slowComponents > 0 ? '#ff4d4f' : '#3f8600' },
                                }}
                            />
                            {stats.profiledComponents > 0 && (
                                <Progress
                                    percent={Math.round(
                                        ((stats.profiledComponents - stats.slowComponents) / stats.profiledComponents) *
                                            100
                                    )}
                                    size="small"
                                    status={stats.slowComponents > 0 ? 'exception' : 'success'}
                                    style={{ marginTop: 8 }}
                                />
                            )}
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Проблемы"
                                value={stats.totalIssues}
                                prefix={<WarningOutlined />}
                                styles={{
                                    content: { color: stats.totalIssues > 0 ? '#ff4d4f' : '#3f8600' },
                                }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* События системы */}
                {events.length > 0 && (
                    <Card title="Последние события" size="small">
                        <Timeline
                            items={events.slice(0, 10).map((event) => ({
                                color: event.type === 'error' ? 'red' : event.type === 'warning' ? 'orange' : 'blue',
                                children: (
                                    <div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8,
                                            }}
                                        >
                                            <Text strong>{event.title}</Text>
                                            {event.source && <Tag style={{ fontSize: '12px' }}>{event.source}</Tag>}
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                {event.timestamp.toLocaleTimeString()}
                                            </Text>
                                        </div>
                                        <Text type="secondary">{event.description}</Text>
                                    </div>
                                ),
                            }))}
                        />
                    </Card>
                )}

                {/* Быстрые действия */}
                <Card title="Быстрые действия" size="small">
                    <Space wrap>
                        <Tag color="blue" style={{ cursor: 'pointer' }} onClick={loadOverviewData}>
                            <SyncOutlined /> Обновить данные
                        </Tag>
                        <Tag color="green" style={{ cursor: 'pointer' }}>
                            <InfoCircleOutlined /> Открыть детальный профайлер
                        </Tag>
                    </Space>
                </Card>
            </Space>
        </div>
    )
})

export default DebugOverview
